import { computed, inject } from 'vue';

/**
 * Site context provided by FactoryContextLayout, which only wraps a page when the ISA95
 * equipment hierarchy is enabled. Outside of that layout nothing is provided, and every value
 * below is empty: consumers must treat that as "no site scoping at all".
 */
export default function useFactoryContext() {
  /**
   * Ids of the selected sites, a single one in the layout's default mode
   */
  const siteIds = inject('parent', null);
  /**
   * Machines of the selected site, resolved by the layout through the equipment hierarchy
   */
  const providedSiteMachines = inject('selectedSiteMachines', null);

  /**
   * Id of the selected site, undefined without site context
   */
  const selectedSiteId = computed(() => siteIds?.value?.[0]);

  /**
   * Machines of the selected site, an empty list without site context. Exposed raw because
   * consumers read different fields of them, so only the injection is shared.
   */
  const siteMachines = computed(() => providedSiteMachines?.value ?? []);

  /**
   * Ids of the machines of the selected site, null without site context. The null matters: it
   * tells a query not to scope itself at all, which an empty list could not express.
   */
  const siteMachineIds = computed(() => (providedSiteMachines
    ? providedSiteMachines.value.map(({ id }) => String(id))
    : null));

  return {
    selectedSiteId,
    siteMachines,
    siteMachineIds,
  };
}
